"use client"

import { FragmentType, graphql, useFragment } from "@/gql"
import { ProjectDesktopContentFragmentDoc } from "@/gql/graphql"
import { useEffect, useLayoutEffect, useState } from "react"
import { ProjectMetadata } from "../components"
import { ProjectDesktopHeader } from "./ProjectDesktopHeader"
import type { NovaSession } from "@/types"
import { ProjectLightbox } from "./ProjectLightbox"
import clsx from "clsx"
import { ProjectMedia } from "./ProjectMedia"

graphql(`
  fragment ProjectDesktopContent on CreativeWorkPortfolioProject {
    id
    files {
      edges {
        node {
          id
          __typename
          metadata {
            video
          }
          ...Video
          ...ProjectMedia
        }
      }
    }

    ...ProjectDesktopHeader
    ...ProjectMetadata
    ...ProjectLightbox
  }
`)

interface Props {
  project: FragmentType<typeof ProjectDesktopContentFragmentDoc> | undefined | null
  userHref: string
  serverSession: NovaSession | null
  isViewersProject: boolean
}

export const ProjectDesktopContent: React.FC<Props> = ({ userHref, serverSession, isViewersProject, ...props }) => {
  const project = useFragment(ProjectDesktopContentFragmentDoc, props.project)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [lightBox, setLightBox] = useState({
    open: false,
    index: 0,
  })

  const isAuthenticated = Boolean(serverSession?.accessToken)

  const onUpdateVisibleIndex = (index: number) => {
    setLightBox({
      open: true,
      index,
    })
  }

  useLayoutEffect(() => {
    if (!hasLoaded) {
      setTimeout(() => {
        setHasLoaded(true)
      }, 300)
    }
  }, [hasLoaded])

  useEffect(() => {
    lightBox.open ? (document.body.style.overflow = "hidden") : (document.body.style.overflow = "")

    const nav = document.getElementById("nav")

    if (nav) {
      if (lightBox.open) {
        nav.style.display = "none"
      } else {
        nav.style.display = "flex"
      }
    }

    return () => {
      document.body.style.overflow = ""
    }
  }, [lightBox.open])

  return (
    <>
      <div className="relative h-screen">
        <ProjectDesktopHeader project={project} serverSession={serverSession} isViewersProject={isViewersProject} />
        <div className="relative grid w-full grid-cols-12 gap-0">
          <div className="col-span-12 lg:col-span-7">
            <div className="h-full w-full sm:pt-8 lg:pr-8">
              <div
                className={clsx(
                  "flex h-full w-full flex-col gap-2 pb-4",
                  isAuthenticated ? "md:max-w-[500px] xl:max-w-[700px]" : "md:max-w-[500px] lg:max-w-[700px]"
                )}>
                {project?.files?.edges.map((edge, index) => {
                  return (
                    <ProjectMedia
                      index={index}
                      file={edge?.node}
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        setLightBox({ open: true, index })
                      }}
                      key={index}
                    />
                  )
                })}
              </div>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="sticky top-[96px] flex h-[calc(100dvh-100px)] w-full flex-col overflow-y-scroll pl-3">
              <div className="pb-4">
                {project && <ProjectMetadata project={project} serverSession={serverSession} />}
              </div>
            </div>
          </div>
        </div>
      </div>
      {lightBox.open && (
        <ProjectLightbox
          visibleIndex={lightBox.index}
          onClose={() =>
            setLightBox({
              open: false,
              index: 0,
            })
          }
          project={project}
          onUpdateVisibleIndex={onUpdateVisibleIndex}
        />
      )}
    </>
  )
}
